import React, { useCallback, useMemo, useState } from 'react';
import useUrlState from '../../../../../../Hooks/useUrlState';
import useTiktokPostInvites, { TiktokPostInviteData } from './useTiktokPostInvites';
import ContactedTable from './ContactedTable/ContactedTable';
import styles from './Contacted.module.css';
import { Button, Card, Modal, Popover, Skeleton } from '@round/ui-kit';
import { microwave } from '@round/api';
import {
    convertOrderByToOrdering,
    convertOrderingToOrderBy,
} from '../../../../../../ui/PaginationTable/PaginationTable.helpers';
import { OrderByParam } from '../../../../../../Hooks/useReactTableSortToOrderBy';
import cn from 'classnames';
import Calendar from '../../../../../../ui/DataEntry/Calendar/Calendar';
import moment from 'moment/moment';
import { showNotification } from '../../../../../../helpers';
import EmailHistory from './EmailHistory/EmailHistory';
import { DebounceInput } from 'react-debounce-input';
import ChaseEmailPreview, { PreviewInvite } from '../../EmailPreview/ChaseEmailPreview/ChaseEmailPreview';

type Props = {
    microwaveCampaignId?: number;
};

type UrlState = {
    page: number;
    page_size: number;
} & Partial<{
    ordering: string;
    number_of_chase_emails_sent: number;
}>;

const initialUrlState: UrlState = {
    page: 1,
    page_size: 10,
    ordering: '-post_create_time',
};

const Contacted = ({ microwaveCampaignId }: Props) => {
    const [urlState, setUrlState] = useUrlState<UrlState>(initialUrlState, { shouldSetMissingInitialValues: true });

    const [selectedCreators, setSelectedCreators] = useState<TiktokPostInviteData[]>([]);
    const [previewCreators, setPreviewCreators] = useState<TiktokPostInviteData[]>([]);
    const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);

    const [isEmailHistoryModalOpen, setIsEmailHistoryModalOpen] = useState(false);
    const [emailHistoryPostInvite, setEmailHistoryPostInvite] = useState<TiktokPostInviteData | null>(null);

    const page = urlState.page ? Number(urlState.page) : initialUrlState.page;
    const pageSize = urlState.page_size ? Number(urlState.page_size) : initialUrlState.page_size;
    const ordering = useMemo(
        () =>
            urlState.ordering && microwave.isGetInvitesOrderingValues(urlState.ordering)
                ? urlState.ordering
                : undefined,
        [urlState.ordering]
    );
    const setOrdering = useCallback(
        (orderBy: OrderByParam<TiktokPostInviteData>) => {
            setUrlState({ ordering: convertOrderByToOrdering(orderBy) });
        },
        [setUrlState]
    );

    const { creators, loading, count, errorLoading, isInitialized, updatePostInvite, reset } = useTiktokPostInvites(
        {
            campaignId: microwaveCampaignId,
            page: page,
            pageSize,
            ordering,
            isPosted: false,
            numberOfChaseEmailsSent: urlState.number_of_chase_emails_sent
                ? Number(urlState.number_of_chase_emails_sent)
                : undefined,
        },
        { isDisabled: !microwaveCampaignId }
    );

    const [rowsDisplayed, setRowsDisplayed] = useState(0);
    const rowsDisplayedText = `displaying ${rowsDisplayed} out of ${count}`;
    const isTableLoading = loading || (!errorLoading && !isInitialized);

    const [multipleDeadlineAnchor, setMultipleDeadlineAnchor] = useState<HTMLButtonElement | null>(null);
    const [multipleDeadlineValue, setMultipleDeadlineValue] = useState(moment().format('YYYY-MM-DD'));
    const [isMultipleDeadlinesSaveLoading, setIsMultipleDeadlinesSaveLoading] = useState(false);

    return (
        <div className={styles.container}>
            <div className={styles.filters}>
                <div className={styles.filter}>
                    <label className={styles.filterLabel}># of chase emails</label>
                    <DebounceInput
                        className={styles.filterInput}
                        type="number"
                        placeholder="Enter # of chase emails..."
                        value={urlState.number_of_chase_emails_sent}
                        onChange={(e) => {
                            setUrlState({
                                number_of_chase_emails_sent: e.target.value ? Number(e.target.value) : undefined,
                                page: 1,
                            });
                            reset();
                        }}
                        debounceTimeout={700}
                    />
                </div>
            </div>
            <div className={styles.actionsRow}>
                <div className={cn(styles.actionButtons, { [styles.visible]: selectedCreators.length > 0 })}>
                    <Button
                        disabled={!microwaveCampaignId}
                        onClick={() => {
                            setPreviewCreators(selectedCreators);
                            setIsPreviewModalOpen(true);
                        }}
                    >
                        Preview {selectedCreators.length} email{selectedCreators.length !== 1 ? 's' : ''}
                    </Button>

                    <Button ref={setMultipleDeadlineAnchor}>Set multiple deadlines</Button>
                    <Popover anchorElement={multipleDeadlineAnchor} showOn="click">
                        {(setShow) => (
                            <Card className={styles.multipleDeadlinesPopover}>
                                <Calendar
                                    value={multipleDeadlineValue}
                                    onChange={setMultipleDeadlineValue}
                                    minDate={new Date()}
                                />

                                <Button
                                    className={styles.multipleDeadlinesSaveButton}
                                    loading={isMultipleDeadlinesSaveLoading}
                                    onClick={async () => {
                                        try {
                                            setIsMultipleDeadlinesSaveLoading(true);
                                            const results = await Promise.allSettled(
                                                selectedCreators.map((postInvite) =>
                                                    updatePostInvite(postInvite.id, { deadline: multipleDeadlineValue })
                                                )
                                            );

                                            const allUpdated = results.every((result) => result.status === 'fulfilled');
                                            if (allUpdated) {
                                                showNotification('all updated', 'info');
                                                setShow(false);
                                                setMultipleDeadlineValue(moment().format('YYYY-MM-DD'));
                                                return;
                                            }

                                            showNotification('Could not update some or all dates', 'error');
                                            return;
                                        } catch {
                                            showNotification('Could not save multiple deadlines', 'error');
                                        } finally {
                                            setIsMultipleDeadlinesSaveLoading(false);
                                        }
                                    }}
                                >
                                    Save
                                </Button>
                            </Card>
                        )}
                    </Popover>
                </div>

                <p className={styles.rowsDisplayedText}>
                    {isTableLoading ? <Skeleton width="7rem" /> : rowsDisplayedText}
                </p>
            </div>

            <ContactedTable
                loading={isTableLoading}
                data={creators}
                count={count}
                page={page}
                setPage={(page) => {
                    setUrlState({ page });
                    reset();
                }}
                orderBy={convertOrderingToOrderBy(ordering)}
                disableSortBy={false}
                onOrderByChange={(ordering) => {
                    setOrdering(ordering);
                    reset();
                }}
                manualSortBy
                pageSize={pageSize}
                setPageSize={(pageSize) => {
                    setUrlState({ page_size: pageSize ?? initialUrlState.page_size });
                    reset();
                }}
                noDataLabel={errorLoading ? "Sorry, we're experiencing technical issues" : 'No data found'}
                onPaginationChange={(state) => setRowsDisplayed(state.pageRows.length)}
                selectedRowIds={selectedCreators.map((c) => c.id)}
                onRowSelect={(creator) =>
                    setSelectedCreators((creators) => {
                        if (creators.find((c) => c.id === creator.id)) {
                            return creators.filter((c) => c.id !== creator.id);
                        }

                        return creators.concat(creator);
                    })
                }
                onAllRowsSelect={(incomingCreators) =>
                    setSelectedCreators((prev) => {
                        if (!incomingCreators.every((creator) => !!prev.find((c) => c.id === creator.id))) {
                            return prev.concat(
                                incomingCreators.filter((c) => !prev.find((creator) => creator.id === c.id))
                            );
                        }

                        return prev.filter((c) => !incomingCreators.find((creator) => creator.id === c.id));
                    })
                }
                updatePostInvite={updatePostInvite}
                onEmailHistoryClicked={(row) => {
                    setEmailHistoryPostInvite(row);
                    setIsEmailHistoryModalOpen(true);
                }}
            />

            {microwaveCampaignId && (
                <Modal
                    className={styles.previewModal}
                    closeOnOverlayClick
                    isOpen={isPreviewModalOpen}
                    onClose={() => setIsPreviewModalOpen(false)}
                >
                    <ChaseEmailPreview
                        campaignId={microwaveCampaignId}
                        creators={previewCreators.map<PreviewInvite>((creator) => ({
                            email: creator.influencerEmailAddress,
                            influencer_id: creator.influencerId,
                            nickname: creator.userData.nickname || creator.influencerEmailAddress || '-',
                            invite_id: creator.id,
                        }))}
                        onEmailSent={(inviteIds) => {
                            setPreviewCreators([]);
                            setSelectedCreators((creators) => creators.filter((c) => !inviteIds.includes(c.id)));
                        }}
                    />
                </Modal>
            )}

            {emailHistoryPostInvite && (
                <Modal
                    closeOnOverlayClick
                    className={styles.emailHistoryModal}
                    isOpen={isEmailHistoryModalOpen}
                    onClose={() => {
                        setIsEmailHistoryModalOpen(false);
                        setEmailHistoryPostInvite(null);
                    }}
                >
                    <EmailHistory contactedCreator={emailHistoryPostInvite} />
                </Modal>
            )}
        </div>
    );
};

export default Contacted;
